import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import axios from 'axios'
import { Toast } from '@skop-app/lib-front'

import { getQuotaErrorMessageKey } from '@/services/errors/quotaErrors'

export const useSendSignRequest = (options?: {
  onSuccess?: (variables: any) => void
  onError?: (error: any) => void
}) => {
  const t = useTranslations()
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['sendDocument'],
    mutationFn: sendSignRequest,
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: ['order', variables?.orderID],
      })
      queryClient.invalidateQueries({
        queryKey: ['request'],
      })
      queryClient.refetchQueries({
        queryKey: ['request'],
      })
      Toast.success(t('Documents-directory.sign_request_sent'), {
        closeButton: true,
        id: 'signRequestSent',
      })
      options?.onSuccess?.(variables)
    },
    onError: (error) => {
      const quotaMessageKey = getQuotaErrorMessageKey(error)
      Toast.error(
        quotaMessageKey
          ? t(`Documents-directory.${quotaMessageKey}`)
          : t('Documents-directory.sign_request_sent_error'),
        {
          closeButton: true,
          id: 'signRequestSentError',
        },
      )
      options?.onError?.(error)
    },
  })
}

export const sendSignRequest = async ({ orderID, body }) => {
  try {
    const { data } = await axios.post('/api/sign/create', { ...body, orderID })
    const status = data.responseData.status || 'PENDING'

    return {
      orderID: orderID,
      signRequestID: data.responseData.id,
      signRequestStatus: status,
    }
  } catch (error: any) {
    console.error('Upload error:', error)
    throw error
  }
}

export const useResendSignRequest = () => {
  const t = useTranslations()
  return useMutation({
    mutationKey: ['resendDocument'],
    mutationFn: resendSignRequest,
    onSuccess: () => {
      Toast.success(t('Documents-directory.sign_request_resent'), {
        closeButton: true,
        id: 'signRequestResent',
      })
    },
    onError: () => {
      Toast.error(t('Documents-directory.sign_request_resent_error'), {
        closeButton: true,
        id: 'signRequestResentError',
      })
    },
  })
}

const resendSignRequest = async (signRequest) => {
  try {
    await axios.post(`/api/sign/resend/${signRequest?.id}`, {
      id: signRequest?.id,
      recipients: signRequest?.recipients,
    })
  } catch (error: any) {
    console.error('Error resending document', error)
    throw error
  }
}

export const useCancelSignRequest = () => {
  const t = useTranslations()
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['cancelSignRequest'],
    mutationFn: cancelSignRequest,
    onMutate: (variables) => {
      queryClient.cancelQueries({ queryKey: ['order', variables.orderID] })
      queryClient.setQueryData(
        ['order', variables.orderID],
        (oldData: object) => {
          return {
            ...oldData,
            yousignID: null,
            yousignStatus: null,
          }
        },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['request'],
      })
      queryClient.refetchQueries({
        queryKey: ['request'],
      })
      Toast.info(t('Documents-directory.sign_request_canceled'), {
        closeButton: true,
        id: 'signRequestCanceled',
      })
    },
    onError: () => {
      Toast.error(t('Documents-directory.sign_request_canceled_error'), {
        closeButton: true,
        id: 'signRequestCanceledError',
      })
    },
  })
}

const cancelSignRequest = async ({
  signRequestID,
  orderID,
}: {
  signRequestID: string
  orderID: string
}) => {
  try {
    await axios.delete(`/api/sign/delete/${signRequestID}`, {
      data: { orderID },
    })

    return { signRequestID, orderID }
  } catch (error: any) {
    console.error('Cancel error:', error)
    throw error
  }
}
